import Evidently from "aws-sdk/clients/evidently";
import PersonalizeRuntime from "aws-sdk/clients/personalizeruntime";
import PersonalizeEvents from "aws-sdk/clients/personalizeevents";

import { STS, AssumeRoleWithWebIdentityCommand } from "@aws-sdk/client-sts";
import { Auth } from "aws-amplify";

import {
  CognitoIdentityClient,
  GetOpenIdTokenCommand,
  GetIdCommand,
} from "@aws-sdk/client-cognito-identity";

const cognitoIdentityClient = new CognitoIdentityClient({
  region: "us-west-2",
});
const stsClient = new STS({ region: "us-west-2" });

const defaultEvidentlyClientBuilder = async (
  endpoint,
  region,
  credentialProvider
) => {
  let credentials = await credentialProvider();
  return new Evidently({
    endpoint,
    region,
    credentials,
  });
};

const defaultPersonalizeRuntimeClientBuilder = async (
  endpoint,
  region,
  credentialProvider
) => {
  let credentials = await credentialProvider();
  return new PersonalizeRuntime({
    endpoint,
    region,
    credentials,
  });
};

const defaultPersonalizeEventsClientBuilder = async (
  endpoint,
  region,
  credentialProvider
) => {
  let credentials = await credentialProvider();
  return new PersonalizeEvents({
    endpoint,
    region,
    credentials,
  });
};

const cognitoCredentialsProvider = async () => {
  return cognitoIdentityClient
    .send(
      new GetIdCommand({
        IdentityPoolId: "us-west-2:eec82313-8c7b-4a09-852e-8d01c6dfaa99",
      })
    )
    .then((getIdResponse) =>
      cognitoIdentityClient.send(
        new GetOpenIdTokenCommand({
          IdentityId: getIdResponse.IdentityId,
        })
      )
    )
    .then((getOpenIdTokenResponse) =>
      stsClient.send(
        new AssumeRoleWithWebIdentityCommand({
          RoleArn:
            "arn:aws:iam::291568649917:role/amplify-authcra-master-205518-unauthRole",
          RoleSessionName: "evidently",
          WebIdentityToken: getOpenIdTokenResponse.Token,
        })
      )
    )
    .then((assumeRoleResponse) => {
      if (!assumeRoleResponse.Credentials) {
        throw new Error("STS response did not contain credentials.");
      }

      const credentials = {
        accessKeyId: assumeRoleResponse.Credentials.AccessKeyId,
        secretAccessKey: assumeRoleResponse.Credentials.SecretAccessKey,
        sessionToken: assumeRoleResponse.Credentials.SessionToken,
        expiration: assumeRoleResponse.Credentials.Expiration,
      };
      return credentials;
    });
};

export const useEvidenltyEffect = (project, feature, value, setValue) => {
  async function fetchFeatures(entityId) {
    const tempClient = await defaultEvidentlyClientBuilder(
      "https://evidently.us-west-2.amazonaws.com",
      "us-west-2",
      cognitoCredentialsProvider
    );

    // API request structure
    const evaluateFeatureRequest = {
      // entityId is a JSON key to store the entity ID in custom event.
      // The entity ID used in evaluateFeature API needs to be the same as the one used in putProjectEvents API with custom event type.
      entityId: entityId || new Date().getTime().toString(),
      feature,
      project,
    };

    // Evaluate feature
    const featureResponse = await tempClient
      .evaluateFeature(evaluateFeatureRequest)
      .promise();

    if (setValue && featureResponse?.value !== undefined) {
      setValue(featureResponse.value);
    }
  }

  if (value === null) {
    Auth.currentCredentials().then((info) => fetchFeatures(info.identityId));
  }
  return () => {};
};

export const sendPutProjectEvent = (project, name, value) => {
  async function putProjectEvent(entityId) {
    const tempClient = await defaultEvidentlyClientBuilder(
      "https://evidently.us-west-2.amazonaws.com",
      "us-west-2",
      cognitoCredentialsProvider
    );

    const metricData = `{
        "details": {
           "${name}": ${value}
        },
        "userDetails": { "entityId": "${
          entityId || new Date().getTime().toString()
        }"}
     }`;

    // API request structure
    const putProjectEventsRequest = {
      project,
      events: [
        {
          timestamp: new Date(),
          type: "aws.evidently.custom",
          data: JSON.parse(metricData),
        },
      ],
    };

    // Put project event
    await tempClient.putProjectEvents(putProjectEventsRequest).promise();
  }

  Auth.currentCredentials().then((info) => putProjectEvent(info.identityId));

  return () => {};
};

export const useRUMInstallEffect = () => {
  const script = document.createElement("script");

  script.innerHTML =
    process.env.NODE_ENV === "development"
      ? `(function(n,i,v,r,s,c,x,z){x=window.AwsRumClient={q:[],n:n,i:i,v:v,r:r,c:c};window[n]=function(c,p){x.q.push({c:c,p:p});};z=document.createElement('script');z.async=true;z.src=s;document.head.insertBefore(z,document.head.getElementsByTagName('script')[0]);})('cwr','b70ee129-e514-43d5-981e-a7a1b92a9ca8','1.0.0','us-west-2','https://client.rum.us-east-1.amazonaws.com/1.0.5/cwr.js',{sessionSampleRate:1,guestRoleArn:"arn:aws:iam::291568649917:role/RUM-Monitor-us-west-2-291568649917-5859163055461-Unauth",identityPoolId:"us-west-2:1e66ba1e-60c3-4812-af64-faf30ee3a136",endpoint:"https://dataplane.rum.us-west-2.amazonaws.com",telemetries:["performance","errors","http"],allowCookies:true,enableXRay:false});`
      : `(function(n,i,v,r,s,c,x,z){x=window.AwsRumClient={q:[],n:n,i:i,v:v,r:r,c:c};window[n]=function(c,p){x.q.push({c:c,p:p});};z=document.createElement('script');z.async=true;z.src=s;document.head.insertBefore(z,document.head.getElementsByTagName('script')[0]);})('cwr','fea817f8-2fa1-44f9-8670-ec281177582e','1.0.0','us-west-2','https://client.rum.us-east-1.amazonaws.com/1.0.5/cwr.js',{sessionSampleRate:1,guestRoleArn:"arn:aws:iam::291568649917:role/RUM-Monitor-us-west-2-291568649917-1221989745461-Unauth",identityPoolId:"us-west-2:60297500-d792-4a1f-ae13-a91db97dd822",endpoint:"https://dataplane.rum.us-west-2.amazonaws.com",telemetries:["performance","errors","http"],allowCookies:true,enableXRay:false});`;

  document.body.appendChild(script);

  return () => {
    document.body.removeChild(script);
  };
};

export const useRUMUpdatePageEffect = (path) => {
  if (window.cwr) {
    window.cwr("recordPageView", path);
  }
  return () => {};
};

export const browserDetect = () => {
  let userAgent = navigator.userAgent;
  let browserName;
  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = "chrome";
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = "firefox";
  } else if (userAgent.match(/safari/i)) {
    browserName = "safari";
  } else if (userAgent.match(/opr\//i)) {
    browserName = "opera";
  } else if (userAgent.match(/edg/i)) {
    browserName = "edge";
  } else {
    browserName = "none";
  }
  return browserName;
};

export const deviceDetect = () => {
    let check = false;
    // eslint-disable-next-line 
    (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor||window.opera);
    return check ? 'mobile' : 'desktop';
}

export const getPersonalizeRelatedRecommendations = (
  itemId,
  value,
  setValue,
  numResults = 4
) => {
  async function personalizeRelatedRecommendations(entityId) {
    const tempClient = await defaultPersonalizeRuntimeClientBuilder(
      "https://personalize-runtime.us-west-2.amazonaws.com",
      "us-west-2",
      cognitoCredentialsProvider
    );

    // API request structure
    const getRecommendationsRequest = {
      campaignArn:
        "arn:aws:personalize:us-west-2:291568649917:campaign/ab-course-related-courses",
      filterArn:
        "arn:aws:personalize:us-west-2:291568649917:filter/ab-course-filter-added-and-finished",
      itemId: itemId,
      numResults,
      userId: entityId || new Date().getTime().toString(),
      context: {
        'DEVICE': deviceDetect(),
        'BROWSER': browserDetect(),
      },
    };

    // Put project event
    const getRecommendationsResponse = await tempClient
      .getRecommendations(getRecommendationsRequest)
      .promise();
    if (getRecommendationsResponse?.itemList) {
      setValue(getRecommendationsResponse?.itemList);
    }
  }

  if (value === null) {
    Auth.currentCredentials().then((info) =>
      personalizeRelatedRecommendations(info.identityId)
    );
  }

  return () => {};
};

export const getPersonalizeRecommendations = (
  value,
  setValue,
  numResults = 4
) => {
  async function personalizeRecommendations(entityId) {
    const tempClient = await defaultPersonalizeRuntimeClientBuilder(
      "https://personalize-runtime.us-west-2.amazonaws.com",
      "us-west-2",
      cognitoCredentialsProvider
    );

    // API request structure
    const getRecommendationsRequest = {
      campaignArn:
        "arn:aws:personalize:us-west-2:291568649917:campaign/ab-course-personalization",
      filterArn:
        "arn:aws:personalize:us-west-2:291568649917:filter/ab-course-filter-added-and-finished",
      numResults,
      userId: entityId || new Date().getTime().toString(),
      context: {
        'DEVICE': deviceDetect(),
        'BROWSER': browserDetect(),
      },
    };

    // Put project event
    const getRecommendationsResponse = await tempClient
      .getRecommendations(getRecommendationsRequest)
      .promise();
    if (getRecommendationsResponse?.itemList) {
      setValue(getRecommendationsResponse?.itemList);
    }
  }

  if (value === null) {
    Auth.currentCredentials().then((info) =>
      personalizeRecommendations(info.identityId)
    );
  }

  return () => {};
};

export const sendPersonalizeEvent = (itemId, eventType) => {
  async function personalizeEvent(entityId) {
    const tempClient = await defaultPersonalizeEventsClientBuilder(
      "https://personalize-events.us-west-2.amazonaws.com",
      "us-west-2",
      cognitoCredentialsProvider
    );

    // API request structure
    const putEventsRequest = {
      eventList: [
        {
          eventType,
          sentAt:
            new Date() ||
            "Wed Dec 31 1969 16:00:00 GMT-0800 (PST)" ||
            123456789,
          itemId: itemId.toString(),
        },
      ],
      sessionId: entityId || new Date().getTime().toString(),
      trackingId: "42edc253-e60e-4801-b02e-a3f6b0eb590b",
      userId: entityId || new Date().getTime().toString(),
    };

    // Put personalize event
    await tempClient.putEvents(putEventsRequest).promise();
  }

  Auth.currentCredentials().then((info) => personalizeEvent(info.identityId));

  return () => {};
};
